import React from 'react';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Calendar, Lightbulb, ArrowLeft, ExternalLink, AlertCircle, Mail, Video, MessageCircle, FileText, Download } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const InterviewSupportSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#02B491]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-[#02B491]" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Interview practice & support
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Jumpstart gives you access to tailored interview prep - make use of it!
            </p>
          </div>
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={onBack}
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-colors ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-lg'
                  : 'bg-[#02B491] text-white hover:bg-[#029078] shadow-md'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-12">
          
          <StepCard title="Book a 1:1 prep call">
            <p className="text-gray-600 mb-6">
              You can book time with a member of the support team to:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Run through your elevator pitch</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Practice case study debriefs or final-round prep</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Ask questions about specific interviews or company expectations</span>
              </li>
            </ul>
            <div className="bg-[#02B491]/5 rounded-xl p-6 border border-[#02B491]/20">
              <h4 className="font-semibold text-gray-900 mb-3">Contact our support team:</h4>
              <p className="text-gray-700">
                <strong>Naim & Edward </strong> - <a 
                  href="https://calendly.com/naim-jumpstart-uk/interview-coaching-generalist-marketing?month=2025-07" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-[#02B491] hover:underline inline-flex items-center"
                >
                  Book interview coaching session
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </p>
            </div>
          </StepCard>



          <StepCard title="Case study helpline">
            <p className="text-gray-600 mb-4">
              Once you've written the <strong>first draft of your case study</strong>, should you be set a task, you can send it to us for feedback.
            </p>
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-yellow-800 font-medium mb-2">Important Guidelines:</p>
                  <ul className="text-yellow-700 space-y-1 text-sm">
                    <li>• Feedback is usually sent within ~24 hours</li>
                    <li>• Only send if you've done a proper draft</li>
                    <li>• Don't just forward the task or ask "what should I do?"</li>
                    <li>• Part of the test is showing initiative and taking a first pass yourself</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-[#02B491]/5 rounded-xl p-6 border border-[#02B491]/20">
              <p className="text-gray-700">
                <strong>Email:</strong> <a 
                  href="mailto:kabir@jumpstart-uk.com" 
                  className="text-[#02B491] hover:underline"
                >
                  kabir@jumpstart-uk.com
                </a>
              </p>
            </div>
          </StepCard>

          <StepCard title="Startup Jargon Buster: Essential Reading Before Your Interview">
            <p className="text-gray-600 mb-4">
              Startups use their own language (what's PMF? MVP?) and they expect you to understand it.
            </p>
            <p className="text-gray-600 mb-4">
              This cheat sheet explains 100+ key terms across business, marketing, finance, tech, and AI, with real examples and short notes on how they relate to your interviews.
            </p>
            <p className="text-gray-600 mb-6">
              If you're preparing for a startup role, make time to read it. You don't want to walk into an interview missing the basics everyone else knows.
            </p>
            <div className="bg-[#02B491]/5 rounded-xl p-6 border border-[#02B491]/20">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-[#02B491]" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Startup Jargon Buster Guide</h4>
                    <p className="text-sm text-gray-600">PDF • Essential startup terminology explained</p>
                  </div>
                </div>
                <a
                  href="/Startup Jargon Buster 2025.11 .pdf"
                  download
                  className="inline-flex items-center px-6 py-3 bg-[#02B491] text-white rounded-lg font-medium hover:bg-[#029078] transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </a>
              </div>
            </div>
          </StepCard>

          <InfoCard
            icon={Lightbulb}
            title="Not getting interviews?"
            description="If you've applied to lots of roles but have had very few or no interviews, email info@jumpstart-uk.com. We can help review your profile and applications, suggest small changes that could improve traction, or advise whether to change tack re: the roles you're applying for. Only reach out if you're genuinely struggling to land interviews because capacity is limited!"
            accent={false}
          />
        </div>
        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewSupportSection; 